import React, { createContext, useState, useContext } from "react";
import { notification } from 'antd';
import { getcompleteinfo } from "../apis/users-api";
import { pushNotificationForVideoCall } from "../apis/push-notifications-api";
import cookieHelper from "../library/helpers/cookieHelper";

const CallContext = createContext();

const sendEndCallNotification = async (callData) => {
  try {
    const patientInfo = await getcompleteinfo(callData.roomName);
    const userDetails = cookieHelper.getUserDetails();

    if (patientInfo?.fcm_token) {
      const notificationData = {
        token: patientInfo.fcm_token,
        notification: {
          title: "Call Ended",
          body: `${userDetails?.firstName || 'Coach'} ${userDetails?.lastName || ''} ended the ${callData.type} call`,
          icon: 'https://i.imgur.com/MZM1ehj.png'
        },
        data: {
          type: "call_ended",
          status: "disconnected",
          call_status: "ended",
          call_type: String(callData.type || ""),
          caller_id: String(callData.roomName || ""),
          caller_name: String(patientInfo.first_name || 'Patient'),
          caller_fcm_token: String(patientInfo.fcm_token || ""),
          coach_id: String(userDetails?.staffId || ""),
          coach_name: String(userDetails?.first_name || 'Coach'),
          timestamp: new Date().toISOString(),
          noti: "true"
        }
      };

      await pushNotificationForVideoCall(notificationData);
    }
  } catch (error) {
    console.error("Error sending FCM notification for end call:", error);
  }
};

export const CallProvider = ({ children }) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isCallConnected, setIsCallConnected] = useState(false);
  const [callData, setCallData] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isCallCleaning, setIsCallCleaning] = useState(false);
  const [callRegistry, setCallRegistry] = useState(new Map());
  const hasShownEndNotification = React.useRef(false);
  const isEndingCall = React.useRef(false);
  const cleanupTimerRef = React.useRef(null);

  const isDuplicateCall = (data) => {
    // Check if we're trying to call the same room/person that's already active
    if (isCallActive && callData?.roomName === data?.roomName) {
      return true;
    }
    
    // Check if this call was recently made (within the last 5 seconds)
    const key = `${data?.roomName}-${data?.type}`;
    const lastCallTime = callRegistry.get(key);
    
    if (lastCallTime && Date.now() - lastCallTime < 5000) {
      return true;
    }
    
    return false;
  };

  const acceptCall = (data) => {
    if (!data?.roomName) {
      console.error('Missing required roomName in call data');
      notification.error({
        message: 'Call Setup Error',
        description: 'Missing room information. Cannot establish call.',
        placement: 'topRight'
      });
      return false;
    }

    if (!data?.type || !['audio', 'video'].includes(data.type)) {
      console.error('Invalid call type');
      notification.error({
        message: 'Call Setup Error',
        description: 'Invalid call type. Must be audio or video.',
        placement: 'topRight'
      });
      return false;
    }

    if (isDuplicateCall(data)) {
      notification.warning({
        message: 'Call Already in Progress',
        description: `${data.type === 'audio' ? 'An audio' : 'A video'} call with this user is already active or was just initiated.`,
        placement: 'topRight'
      });
      return false;
    }

    hasShownEndNotification.current = false;
    isEndingCall.current = false;

    const key = `${data.roomName}-${data.type}`;
    setCallRegistry(prev => {
      const newRegistry = new Map(prev);
      newRegistry.set(key, Date.now());
      return newRegistry;
    });
    
    setIsCallActive(true);
    setIsCallConnected(false);
    setCallData({
      ...data,
      communication: data.type,
      timestamp: Date.now()
    });
    
    notification.success({
      message: 'Call Initiated',
      description: `Starting ${data.type} call with ${data.callerName || 'participant'}`,
      placement: 'topRight',
      duration: 2
    });
    
    return true;
  };


  const endCall = async () => {
  // Prevent duplicate execution
  if (isEndingCall.current) {
    return;
  }

  isEndingCall.current = true;

  try {
    if (callData) {
      const key = `${callData.roomName}-${callData.type}`;

      setCallRegistry(prev => {
        const newRegistry = new Map(prev);
        newRegistry.set(key, Date.now());
        return newRegistry;
      });

      if (!hasShownEndNotification.current) {
        hasShownEndNotification.current = true;

        notification.info({
          message: 'Call Ended',
          description: `${callData.type} call has been terminated`,
          placement: 'topRight',
          duration: 2,
        });
      }

      // Asynchronously trigger FCM call ended notification
      await sendEndCallNotification(callData);
    }

    setIsCallActive(false);
    setIsCallConnected(false);
    setCallData(null);
    setIsMaximized(false);
   
     // Keep buttons disabled for 5s after call ends for room cleanup
    setIsCallCleaning(true);
    if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
    cleanupTimerRef.current = setTimeout(() => {
      setIsCallCleaning(false);
    }, 5000);
  } catch (error) {
    console.error("Error while ending call:", error);
  }
  };


  const toggleMaximize = () => {
    if (isCallActive && callData) {
      setIsMaximized(prev => !prev);
    }
  };

  // Clean up old entries from registry
  React.useEffect(() => {
    const intervalId = setInterval(() => {
      setCallRegistry(prev => {
        const now = Date.now();
        const newRegistry = new Map();
        
        // Only keep entries from the last 10 seconds
        for (const [key, timestamp] of prev.entries()) {
          if (now - timestamp < 10000) {
            newRegistry.set(key, timestamp);
          }
        }
        
        return newRegistry;
      });
    }, 10000); // Run cleanup every 10 seconds
    
    return () => clearInterval(intervalId);
  }, []);

  return (
    <CallContext.Provider 
      value={{ 
        isCallActive,
        isCallConnected,
        setIsCallConnected,
        isCallCleaning,
        callData, 
        isMaximized,
        acceptCall, 
        endCall,
        toggleMaximize,
        isDuplicateCall
      }}
    >
      {children}
    </CallContext.Provider>
  );
};

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
}; 